// src/Vacations.jsx
import React, { useEffect, useState, useRef } from 'react';
import translations from './lang';
import { apiRequest } from './utils';
import './Vacations.css';        // ⟵ ÚJ: kártya stílus
import './ServiceCard.css';
import './Bookings.css';

export default function Vacations() {
  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // date-only UI; mentéskor alakítjuk ISO DateTime-re
  const [form, setForm] = useState({
    id: null,
    start_date: '',
    end_date: '',
    note: '',
    all_day: true,
  });

  const startRef = useRef(null);

  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  };

  // Helper: DD.MM.YYYY
  const formatDMY = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API}/api/vacations/`, {
        method: 'GET',
        headers,
      });
      const data = await res.json().catch(() => []);
      const rows = Array.isArray(data) ? data : (data?.data || []);
      setItems(rows);
    } catch (e) {
      console.error('Vacations fetch error:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);
  useEffect(() => { if (showModal) setTimeout(() => startRef.current?.focus(), 0); }, [showModal]);

  const openNew = () => {
    setForm({ id: null, start_date: '', end_date: '', note: '', all_day: true });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      start_date: (row.start_at || '').slice(0, 10),
      end_date: (row.end_at || '').slice(0, 10),
      note: row.note || '',
      all_day: row.all_day ?? true,
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      alert(t.required || 'Kötelező mező hiányzik');
      return;
    }

    // ISO idő az adott nap elejére / végére UTC-ben (Z)
    const start_at = new Date(`${form.start_date}T00:00:00`).toISOString();
    const end_at   = new Date(`${form.end_date}T23:59:59`).toISOString();

    const body = JSON.stringify({
      start_at,
      end_at,
      all_day: form.all_day,
      note: form.note,
      // opcionális: service: <id>
    });

    try {
      const url = form.id
        ? `${process.env.REACT_APP_API}/api/vacations/${form.id}/`
        : `${process.env.REACT_APP_API}/api/vacations/`;

      const res = await apiRequest(url, { method: form.id ? 'PUT' : 'POST', headers, body });
      const raw = await res.text();
      const data = (() => { try { return JSON.parse(raw); } catch { return {}; } })();

      if (!res.ok) {
        const msg = data?.error || data?.detail || raw || 'Save failed';
        throw new Error(msg);
      }
      await load();
      setShowModal(false);
    } catch (e) {
      console.error('Save error:', e);
      alert((t.general_error || 'Hiba: ') + e.message);
    }
  };

  const removeItem = async (row) => {
    if (!window.confirm(t.confirm_delete || 'Biztosan törlöd?')) return;
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API}/api/vacations/${row.id}/`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const raw = await res.text();
        let data = {};
        try { data = JSON.parse(raw); } catch {}
        throw new Error(data?.error || data?.detail || raw || 'Delete failed');
      }
      await load();
    } catch (e) {
      console.error('Delete error:', e);
      alert((t.general_error || 'Hiba: ') + e.message);
    }
  };

  return (
    <div className="vacations">
      <h3>{t.vacations || 'Szabadságok'}</h3>

      {loading ? (
        <p>{t.loading_data || 'Betöltés...'}</p>
      ) : (
        <div className="vacation-cards">
          {/* üres állapot */}
          {!items?.length && (
            <div className="vacation-card empty-card" onClick={openNew}>
              <div className="vacation-card-content">
                <h4>{t.add || 'Hozzáadás'}</h4>
                <p>{t.add_vacation_desc || 'Új szabadság rögzítése'}</p>
              </div>
            </div>
          )}

          {/* kártyák */}
          {items.map((row) => {
            const range = `${formatDMY(row.start_at)} – ${formatDMY(row.end_at)}`;
            return (
              <div className="vacation-card" key={row.id}>
                <div className="vacation-card-header">
                  <h4 className="vacation-range">{range}</h4>
                  {row.all_day && <span className="badge-all-day">{t.all_day || 'Egész nap'}</span>}
                </div>

                {/* ha a backend küldi, megjelenítjük a szolgáltatás nevét */}
                {row.service_name && (
                  <p className="vacation-service">
                    <strong>{t.service || 'Szolgáltatás'}:</strong> {row.service_name}
                  </p>
                )}

                <p className="vacation-note">
                  <strong>{t.note || 'Megjegyzés'}:</strong> {row.note || '-'}
                </p>

                <div className="vacation-card-buttons">
                  <button onClick={() => openEdit(row)}>{t.edit || 'Szerkesztés'}</button>
                  <button onClick={() => removeItem(row)}>{t.delete || 'Törlés'}</button>
                </div>
              </div>
            );
          })}

          {/* Új kártya a lista végén is */}
          {!!items?.length && (
            <div className="vacation-card empty-card" onClick={openNew}>
              <div className="vacation-card-content">
                <h4>{t.add || 'Hozzáadás'}</h4>
                <p>{t.add_vacation_desc || 'Új szabadság rögzítése'}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal – Új / Szerkesztés */}
      {showModal && (
        <div className="modal" role="dialog" aria-modal="true" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>{form.id ? (t.edit || 'Szerkesztés') : (t.add || 'Hozzáadás')}</h4>

            <label>
              {t.from || 'Kezdete'}:
              <input
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                ref={startRef}
                required
              />
            </label>

            <label>
              {t.to || 'Vége'}:
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                required
              />
            </label>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.all_day}
                onChange={(e) => setForm((f) => ({ ...f, all_day: e.target.checked }))}
              />{' '}
              {t.all_day || 'Egész nap'}
            </label>

            <label>
              {t.note || 'Megjegyzés'}:
              <input
                type="text"
                name="note"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder={t.note || 'Megjegyzés'}
              />
            </label>

            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button onClick={save}>{form.id ? (t.save || 'Mentés') : (t.add || 'Hozzáadás')}</button>
              <button onClick={() => setShowModal(false)}>{t.cancel || 'Mégse'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
